/**
 * The 免税 buyer's passport, typed by the cashier (phase 1 — T07-30) or read by a scanner that
 * types the two MRZ lines (keyboard mode). Checked here only for "is it filled in"; the check
 * digits of an MRZ and every rule (非居住者, 上陸 ≤ 6 months, ¥5,000) are the server's.
 */
import { z } from "zod";

import type { TaxFreeVerifyBody } from "@/lib/types";

export const STATUSES = [
  "temporary_visitor",
  "diplomat",
  "official",
  "landing_permit",
  "jp_nonresident",
] as const;
export type ResidenceStatus = (typeof STATUSES)[number];

/** Passport country codes (ICAO alpha-3 as printed) offered first; any other can be typed. */
export const COMMON_NATIONALITIES = [
  "CHN",
  "TWN",
  "KOR",
  "HKG",
  "USA",
  "THA",
  "SGP",
  "MYS",
  "PHL",
  "VNM",
  "IDN",
  "AUS",
  "GBR",
  "FRA",
  "DEU",
  "CAN",
  "JPN",
] as const;

const ALPHA2: Record<string, string> = {
  CHN: "CN", TWN: "TW", KOR: "KR", HKG: "HK", USA: "US", THA: "TH", SGP: "SG", MYS: "MY",
  PHL: "PH", VNM: "VN", IDN: "ID", AUS: "AU", GBR: "GB", FRA: "FR", DEU: "DE", CAN: "CA",
  JPN: "JP",
}; // prettier-ignore

export function countryName(code: string, locale: string): string {
  const alpha2 = ALPHA2[code];
  if (!alpha2) {
    return code;
  }
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(alpha2) ?? code;
  } catch {
    return code;
  }
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const NAME = /^[A-Z][A-Z '\-.]*$/;

export interface PassportDraft {
  mrz: string;
  passportNumber: string;
  nationality: string;
  surname: string;
  givenName: string;
  dateOfBirth: string;
  passportExpiry: string;
  entryDate: string;
  statusOfResidence: ResidenceStatus;
  landingPermitType: string;
  proofKind: "zairyu_shomei" | "koseki_fuhyo";
  departureDate: string;
}

export function emptyPassport(): PassportDraft {
  return {
    mrz: "",
    passportNumber: "",
    nationality: "",
    surname: "",
    givenName: "",
    dateOfBirth: "",
    passportExpiry: "",
    entryDate: "",
    statusOfResidence: "temporary_visitor",
    landingPermitType: "",
    proofKind: "zairyu_shomei",
    departureDate: "",
  };
}

/** The two MRZ lines of a passport (TD3: 2 × 44 characters) — spaces / line breaks tolerated. */
export function mrzLines(raw: string): string | null {
  const lines = raw
    .toUpperCase()
    .split(/\r?\n/)
    .map((l) => l.replace(/\s/g, ""))
    .filter(Boolean);
  if (lines.length === 1 && lines[0]?.length === 88) {
    return `${lines[0].slice(0, 44)}\n${lines[0].slice(44)}`;
  }
  if (lines.length === 2 && lines.every((l) => l.length === 44 && /^[A-Z0-9<]+$/.test(l))) {
    return lines.join("\n");
  }
  return null;
}

const manualSchema = z
  .object({
    passportNumber: z.string().regex(/^[A-Z0-9]{5,20}$/, "passport_number"),
    nationality: z.string().regex(/^[A-Z]{2,3}$/, "nationality"),
    surname: z.string().regex(NAME, "surname"),
    givenName: z.string().regex(/^([A-Z][A-Z '\-.]*)?$/, "given_name"),
    dateOfBirth: z.string().regex(DATE, "date_of_birth"),
    passportExpiry: z.string().regex(DATE, "passport_expiry").or(z.literal("")),
    entryDate: z.string().regex(DATE, "entry_date"),
    statusOfResidence: z.enum(STATUSES),
    landingPermitType: z.string(),
    proofKind: z.enum(["zairyu_shomei", "koseki_fuhyo"]),
    departureDate: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.statusOfResidence === "jp_nonresident" && !DATE.test(value.departureDate)) {
      ctx.addIssue({ code: "custom", path: ["departureDate"], message: "departure_date" });
    }
    if (value.statusOfResidence === "landing_permit" && !value.landingPermitType.trim()) {
      ctx.addIssue({ code: "custom", path: ["landingPermitType"], message: "landing_permit_type" });
    }
  });

export type PassportErrors = Partial<Record<keyof PassportDraft, string>>;

/** Upper-case what the passport prints in capitals (names, number, country). */
export function normalize(draft: PassportDraft): PassportDraft {
  return {
    ...draft,
    passportNumber: draft.passportNumber.toUpperCase().replace(/\s/g, ""),
    nationality: draft.nationality.toUpperCase().trim(),
    surname: draft.surname.toUpperCase().trim(),
    givenName: draft.givenName.toUpperCase().trim(),
  };
}

/**
 * The verify request of the draft: the MRZ lines when a scanner gave them (+ 在留資格 / 上陸年月日
 * from the landing sticker), else the typed fields. `errors` = the fields still to fill in.
 */
export function toVerifyBody(input: PassportDraft): {
  body: TaxFreeVerifyBody | null;
  errors: PassportErrors;
} {
  const draft = normalize(input);
  const mrz = mrzLines(draft.mrz);
  const proof =
    draft.statusOfResidence === "jp_nonresident"
      ? { jpNonresidentProof: { kind: draft.proofKind, departureDate: draft.departureDate } }
      : {};
  const permit =
    draft.statusOfResidence === "landing_permit" && draft.landingPermitType.trim()
      ? { landingPermitType: draft.landingPermitType.trim() }
      : {};
  if (mrz) {
    const errors: PassportErrors = {};
    if (!DATE.test(draft.entryDate)) {
      errors.entryDate = "entry_date";
    }
    if (draft.statusOfResidence === "jp_nonresident" && !DATE.test(draft.departureDate)) {
      errors.departureDate = "departure_date";
    }
    if (Object.keys(errors).length > 0) {
      return { body: null, errors };
    }
    return {
      body: {
        passportMrz: mrz,
        statusOfResidence: draft.statusOfResidence,
        entryDate: draft.entryDate,
        ...permit,
        ...proof,
      },
      errors: {},
    };
  }
  const parsed = manualSchema.safeParse(draft);
  if (!parsed.success) {
    const errors: PassportErrors = {};
    for (const issue of parsed.error.issues) {
      const field = issue.path[0] as keyof PassportDraft | undefined;
      if (field && !errors[field]) {
        errors[field] = issue.message;
      }
    }
    if (draft.mrz.trim()) {
      errors.mrz = "mrz";
    }
    return { body: null, errors };
  }
  return {
    body: {
      manual: {
        passportNumber: draft.passportNumber,
        nationality: draft.nationality,
        surname: draft.surname,
        givenName: draft.givenName,
        dateOfBirth: draft.dateOfBirth,
        ...(draft.passportExpiry ? { passportExpiry: draft.passportExpiry } : {}),
        entryDate: draft.entryDate,
        statusOfResidence: draft.statusOfResidence,
        ...permit,
        ...proof,
      },
    },
    errors: {},
  };
}
